/**
 * Kalshi 2025 Market Maker Program — maker-fee rebate math.
 *
 * Source: docs/kalshi-mm-rebate-reference.md (Appendix A, Schedule II,
 * updated 12/11/2025).
 *
 * The rebate is a PERCENTAGE REDUCTION OF THE STANDARD MAKER FEE — not a
 * rebate on premium notional. Per the reference's Fee math section:
 *
 *     effective maker fee = maker_fee × (1 − rebate_pct)
 *
 * and the rebate fraction is governed purely by game state / series:
 *   - Pregame (all sports AND non-sports markets): 50%
 *   - Live / in-game (all sports): 100% — maker fees fully refunded
 *   - Nathan's Hot Dog series: 100% regardless of state
 *
 * What is intentionally NOT modeled here: Option tiers (1–4), per-market
 * max-spread / min-size requirements, availability windows, and the rolling
 * 30-day incentive caps. Those decide whether a given quote *qualifies* for
 * the rebate — they don't change the per-fill fee reduction. They belong in
 * a separate advisory eligibility/tier surface, keyed to the org's program
 * enrollment, not in this pure fee-reduction math.
 *
 * This supersedes the legacy `kalshiMakerRebateBps` notional model in
 * fees.ts (priceCents × count × bps / 10_000), which only ever approximated
 * the program and used the wrong base (premium notional rather than the
 * maker fee). See [[project_kalshi_mm_rebate_model]].
 */

/** Maker-fee rebate fraction for pregame markets (all sports + non-sports). */
export const PREGAME_MAKER_REBATE_PCT = 0.5;
/** Maker-fee rebate fraction for live / in-game markets — fully refunded. */
export const LIVE_MAKER_REBATE_PCT = 1.0;

/**
 * Series that earn the full 100% maker-fee rebate regardless of game state
 * (Section 3 / table note in the reference). Match against the Kalshi series
 * prefix, e.g. the `KXNATHANSHD` in `KXNATHANSHD-25`.
 */
export const FULL_REBATE_SERIES: ReadonlySet<string> = new Set([
  'KXNATHANSHD',
  'KXNATHANDOGS',
]);

/**
 * Maker-fee rebate fraction (0..1) for a market under the MM program.
 *
 *   - `isLive`     → the game's in-play state. Live ⇒ 100%.
 *   - `seriesCode` → Kalshi series prefix; only consulted to catch the
 *                    always-100% specials (Nathan's). Pregame sports and all
 *                    non-sports markets are 50%, so they need no branch — a
 *                    non-live market falls through to the pregame rate.
 */
export function makerRebatePct(args: {
  isLive: boolean;
  seriesCode?: string | null;
}): number {
  const { isLive, seriesCode } = args;
  if (seriesCode && FULL_REBATE_SERIES.has(seriesCode)) return LIVE_MAKER_REBATE_PCT;
  return isLive ? LIVE_MAKER_REBATE_PCT : PREGAME_MAKER_REBATE_PCT;
}

/**
 * Split a standard maker fee into the program rebate and the trader's net
 * (out-of-pocket) maker fee after rebate:
 *
 *   rebateCents = makerFeeCents × rebatePct
 *   netFeeCents = makerFeeCents − rebateCents = makerFeeCents × (1 − rebatePct)
 *
 * `makerFeeCents` is the standard maker fee already computed from the
 * per-series schedule (estimateAggregateFeeCents / estimateOrderFeeCents with
 * the MAKER rate). `rebatePct` comes from {@link makerRebatePct} and is
 * clamped to [0, 1].
 *
 * Returns a zero rebate (and the fee unchanged, floored at 0) when there's
 * nothing to rebate — a non-positive maker fee (e.g. a plain `quadratic`
 * series where makers pay nothing) or a non-positive rebate fraction.
 */
export function applyMakerRebate(args: {
  makerFeeCents: number;
  rebatePct: number;
}): { rebateCents: number; netFeeCents: number } {
  const { makerFeeCents, rebatePct } = args;
  if (!(makerFeeCents > 0) || !(rebatePct > 0)) {
    return { rebateCents: 0, netFeeCents: Math.max(makerFeeCents, 0) };
  }
  const pct = Math.min(rebatePct, 1);
  const rebateCents = makerFeeCents * pct;
  return { rebateCents, netFeeCents: makerFeeCents - rebateCents };
}
